// src/Contexts/TenantContext.jsx

import React, {
  useEffect,
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";
import { useAuth } from "../../Utility/AuthProvider";

export const TenantContext = createContext();

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used within a TenantProvider");
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { jwt } = useAuth();
  const [tenants, setTenants] = useState([]); // Unified state for all tenants
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [tenantError, setTenantError] = useState(null);

  const lastFetchTime = useRef(0);
  const CACHE_LIFETIME = 5 * 60 * 1000; // 5 minutes

  const fetchAllTenants = useCallback(
    async (forceRefresh = false) => {
      if (
        !forceRefresh &&
        Date.now() - lastFetchTime.current < CACHE_LIFETIME &&
        tenants.length > 0
      ) {
        console.log("Serving tenants from cache.");
        return;
      }
      if (!jwt) return;

      setIsLoadingTenants(true);
      setTenantError(null);
      try {
        const response = await APICall(
          "GET",
          paths.tenant.fetchAll,
          undefined,
          undefined,
          jwt
        );
        if (response.status === "success" && Array.isArray(response.response)) {
          setTenants(response.response);
          lastFetchTime.current = Date.now();
        } else {
          setTenantError(response.message || "Failed to fetch tenants.");
        }
      } catch (error) {
        setTenantError("An unexpected error occurred while fetching tenants.");
      } finally {
        setIsLoadingTenants(false);
      }
    },
    // ✅ This function is stable and only depends on jwt.
    [jwt]
  );

  const addTenant = useCallback(
    async (newTenantPayload) => {
      if (!jwt) return { success: false, message: "Not authenticated." };

      try {
        const response = await APICall(
          "POST",
          paths.tenant.add,
          newTenantPayload,
          undefined,
          jwt
        );
        if (response.status === "success") {
          await fetchAllTenants(true); // Force refresh the list
          return { success: true, data: response.response };
        }
        return { success: false, message: response.message };
      } catch (error) {
        return { success: false, message: error.message };
      }
    },
    [jwt, fetchAllTenants]
  );

  const updateTenant = useCallback(
    async (tenantId, updatedTenantPayload) => {
      if (!jwt) return { success: false, message: "Not authenticated." };

      const payload = { ...updatedTenantPayload, tenant_id: tenantId };
      try {
        const response = await APICall(
          "PUT",
          paths.tenant.update,
          payload,
          undefined,
          jwt
        );
        if (response.status === "success") {
          await fetchAllTenants(true); // Force refresh the list
          return { success: true };
        }
        return { success: false, message: response.message };
      } catch (error) {
        return { success: false, message: error.message };
      }
    },
    [jwt, fetchAllTenants]
  );

  const deleteTenant = useCallback(
    async (tenantId) => {
      if (!jwt) return { success: false, message: "Not authenticated." };

      try {
        // Assuming your API needs tenant_id in the body or URL params
        const response = await APICall(
          "DELETE",
          paths.tenant.delete,
          { tenant_id: tenantId },
          undefined,
          jwt
        );
        if (response.status === "success") {
          await fetchAllTenants(true); // Force refresh the list
          return { success: true };
        }
        return { success: false, message: response.message };
      } catch (error) {
        return { success: false, message: error.message };
      }
    },
    [jwt, fetchAllTenants]
  );

  const resetTenantData = useCallback(() => {
    setTenants([]);
    setTenantError(null);
    lastFetchTime.current = 0;
    console.log("TenantContext: State has been reset due to logout.");
  }, []);

  useEffect(() => {
    if (jwt) {
      fetchAllTenants();
    } else {
      resetTenantData();
    }
  }, [jwt, fetchAllTenants, resetTenantData]);

  const value = {
    tenantData: tenants, // Exporting under `tenantData` for compatibility with your components
    isLoadingTenants,
    tenantError,
    fetchAllTenants,
    addTenant,
    updateTenant,
    deleteTenant,
    resetTenantData,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
